import { useQuery } from "@tanstack/react-query";
import { getPublicFileURL } from "@tellescope/utilities";
import { validateUrl } from "@tellescope/utils";
import { useTellescopeSession } from "../../context";
import { useSecureFileUrl } from "./use-secure-file-url";

// `getPublicFileURL` picks its bucket's environment prefix by comparing the host against the two
// known API URLs with `===`, silently falling through to `test/` on any mismatch. A trailing slash
// or differing case is enough to point a production viewer at an empty bucket, so the host is
// normalized to that exact form before it is handed over.
const normalizeApiHost = (host: string | undefined): string | undefined =>
  host ? host.trim().replace(/\/+$/, "").toLowerCase() : undefined;

const publicUrlIsReadable = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: "GET", headers: { Range: "bytes=0-0" } });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Resolves a readable URL for a **staff-authored** file that an enduser may need to view, trying the
 * public asset bucket before the authenticated download endpoint.
 *
 * Prefer plain `useSecureFileUrl` for anything else. This hook exists for the narrow case of a file
 * uploaded once by staff and shown to every enduser who reaches it — a form's consent PDF, say —
 * where the viewer's session may be **path-confined** and unable to reach `/files` at all. A
 * `public-form`-scoped session (a patient following a public form link, or chained from one) is
 * denied that route, so resolving such a file securely renders nothing for exactly those patients.
 *
 * The public bucket needs no session, so it is tried first; it only resolves for files staff
 * uploaded with `publicRead: true`. When that object is not readable, this falls back to
 * `file_download_URL`, which covers privately-uploaded files for any session that can reach it.
 *
 * Returns undefined while resolving and when neither path yields a readable URL.
 *
 * @param secureName - The file's `secureName`, or a URL, which is passed through untouched
 * @param options.preferInBrowser - Sign the secure fallback URL for inline rendering rather than
 *   download. Has no effect on the public URL, which never carries a content disposition.
 *
 * @example
 * ```tsx
 * const pdfUrl = usePublicOrSecureFileUrl(field.options.pdfAttachment, { preferInBrowser: true });
 * return pdfUrl ? <iframe src={pdfUrl} title="Document to sign" /> : null;
 * ```
 */
export const usePublicOrSecureFileUrl = (
  secureName?: string,
  { preferInBrowser }: { preferInBrowser?: boolean } = {}
): string | undefined => {
  const session = useTellescopeSession();
  const businessId = session?.userInfo?.businessId;
  const isAlreadyUrl = !!secureName && !validateUrl(secureName).error;

  const publicUrl =
    secureName && businessId && !isAlreadyUrl
      ? getPublicFileURL({ businessId, name: secureName, apiURL: normalizeApiHost(session?.host) })
      : undefined;

  const { data: isPublicReadable } = useQuery({
    queryKey: ["public-file-readable", publicUrl],
    queryFn: () => publicUrlIsReadable(publicUrl ?? ""),
    enabled: !!publicUrl,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  const secureUrl = useSecureFileUrl(isAlreadyUrl || isPublicReadable === false ? secureName : undefined, {
    preferInBrowser,
  });

  if (isAlreadyUrl) return secureUrl;
  if (isPublicReadable === undefined) return undefined;
  return isPublicReadable ? publicUrl : secureUrl;
};
